<template>
	<div v-if="!!orderId">
		<transition name="modal">
			<div class="modal-mask">
				<div
					class="modal-container flex flex-col relative h-full bg-white"
					style="max-height: 900px; min-width: 550px; border-radius: 25px;"
				>
					<svg
						viewBox="0 0 500 300"
						preserveAspectRatio="none"
						class="absolute top-0 w-full"
						style="border-radius: 25px;"
					>
						<path d="M0,0v249.8c166.7,66.7,333.3,66.7,500,0V0H0Z" :fill="getHeaderColor" />
					</svg>
					<app-icon
						class="absolute top-0 right-0 text-white m-3 cursor-pointer z-10"
						icon="cancel"
						@click="$emit('close')"
					/>
					<div class="absolute flex flex-col h-full w-full p-10 overflow-y-auto">
						<div
							v-if="!isManagingOneBusiness && order.restaurant"
							class="text-white text-3xl font-bold text-center"
						>
							{{ order.restaurant.restaurantName }}
						</div>
						<div class="text-white text-3xl font-bold text-center mb-4">{{ getHeaderText }}</div>
						<div class="relative m-auto w-full flex-1 bg-white rounded-lg shadow-lg">
							<div v-if="order.date" class="flex flex-col gap-3 p-4">
								<div class="flex flex-col">
									<small-order-details
										v-if="order.enableCookingTime && !order.b2bOrder"
										:order="order"
									/>
									<div v-else class="flex flex-col gap-2">
										<div class="text-2xl flex items-center justify-between">
											<div class="flex items-center gap-3">
												<span class="font-bold">#{{ formatOrderId }} </span>
												<span
													v-if="order.firstOrder"
													class="flex text-base items-center gap-1 py-1 px-3 rounded-lg text-white bg-success shadow-md"
												>
													New
												</span>
											</div>
											<div class="flex items-center gap-3">
												<span>ETA: {{ calcEta }}</span>
												<span
													v-if="order.isScheduledOrder"
													class="py-2 px-3 text-base flex gap-1 items-center bg-success rounded-full text-white"
												>
													<app-icon icon="clock" class="text-white" />
													<span>Schedule</span>
												</span>
											</div>
										</div>
										<order-status-progress-bar
											:order-status="order.status"
											height-size="2"
											gap-size="1"
										/>
									</div>
									<div class="flex items-center gap-3 mt-6 mb-2">
										<span class="font-semibold">Porosia</span>
										<span v-for="(type, i) in getOrderTypes" :key="i">
											<div
												v-if="type.label === 'Card'"
												class="flex items-center gap-1 py-1 px-3 rounded-full bg-background shadow-md"
											>
												<img
													alt="credit-card"
													class="w-5 h-auto"
													src="../../assets/images/credit-card.svg"
												/>
												<span>{{ type.label }}</span>
											</div>
											<div
												v-if="type.label === 'Cashless'"
												class="flex items-center gap-1 py-1 px-3 rounded-full bg-background shadow-md"
											>
												<img
													src="../../assets/images/cashless.png"
													alt="cashless"
													class="w-5 h-auto"
												/>
												<span>{{ type.label }}</span>
											</div>
											<div
												v-else-if="type.label !== 'Card'"
												class="flex items-center gap-1 py-1 px-3 rounded-full bg-background shadow-md"
											>
												<app-icon :icon="type.icon" />
												<span>{{ $filters.capitalization(type.label) }}</span>
											</div>
										</span>
									</div>
									<app-line class="my-3" />
									<div class="overflow-y-auto pr-2" style="max-height: 190px">
										<div v-for="item in order.items" :key="item.id">
											<div class="flex items-center justify-between gap-3">
												<div class="flex items-center gap-2">
													<img
														v-if="$filters.orderItemImage(item)"
														:src="$filters.setImage($filters.orderItemImage(item))"
														:alt="item.itemName"
														class="w-10 h-10 flex-shrink-0 rounded-md object-cover cursor-pointer"
														@click="showImage(item)"
													/>
													<div
														v-else
														class="w-10 h-10 flex-shrink-0 rounded-md bg-gray-200 flex items-center justify-center"
													>
														<app-icon icon="food" class="text-secondary" />
													</div>
													<span>{{ item.quantity }}x {{ item.itemName }}</span>
												</div>
												<span class="whitespace-nowrap">{{
													$filters.formatCurrency(
														item.itemPrice * item.quantity,
														order.currency
													)
												}}</span>
											</div>
											<div
												v-if="item.notes"
												class="inline-flex gap-2 items-center border-1 border-gray-300 bg-yellow-100 rounded-lg px-2 py-1"
											>
												<app-icon icon="doc" class="text-tiny text-secondary" />
												<span class="text-tiny">{{ item.notes }}</span>
											</div>
										</div>
									</div>
									<app-line class="my-3" />
									<div class="flex flex-col mr-2">
										<div class="flex justify-between">
											<span>Subtotal</span>
											<span class="w-32 text-right">
												{{ $filters.formatCurrency(order.totalNet, order.currency) }}</span
											>
										</div>
										<div class="flex justify-between">
											<span>Transport</span>
											<div class="flex w-32 justify-end">
												<span>{{
													$filters.formatCurrency(order.transportCost, order.currency)
												}}</span>
											</div>
										</div>
										<div v-if="order.discountValue" class="flex justify-between">
											<span>Discount</span>
											<span class="w-32 text-right">
												<span class="bg-success text-white px-2 py-px rounded-sm mr-3">
													<app-icon icon="gift" class="text-white" />
												</span>
												<span>
													{{
														$filters.formatCurrency(order.discountValue, order.currency)
													}}</span
												>
											</span>
										</div>
										<div class="flex justify-between">
											<span>Total</span>
											<span class="w-32 text-right font-semibold">{{
												$filters.formatCurrency(order.total, order.currency)
											}}</span>
										</div>
									</div>
									<app-line class="my-3" />
									<span class="font-semibold">{{ $t('customer_address') }}</span>
									<div class="h-12 overflow-y-auto">
										<p>{{ order.address }}</p>
										<p>{{ order.addressDetails }}</p>
									</div>
									<div v-if="order.description">
										<app-line class="my-2" />
										<span class="font-semibold">Order Details</span>
										<p
											class="h-12 overflow-y-auto bg-yellow-100 p-1 rounded-lg border border-1 border-yellow-300"
										>
											{{ order.description || 'None' }}
										</p>
									</div>
								</div>
							</div>
							<app-loading-noodles v-else />
						</div>
						<div
							v-if="order.orderID && order.status === statuses.pending"
							class="flex flex-col items-center justify-between mt-5"
						>
							<span class="font-bold">{{ $t('will_you_accept_the_order') }}</span>
							<div class="flex mt-4 gap-6">
								<app-button
									:icon="!loading ? '' : 'spin-loading'"
									class="btn btn-success w-40 h-12 rounded-full text-18 font-semibold"
									@click="acceptOrderAndSetTime"
								>
									{{ !loading ? `${$t('yes')} 👍` : '' }}</app-button
								>
								<app-button
									class="btn btn-primary w-40 h-12 rounded-full text-18 font-semibold"
									@click="$emit('rejectOrder')"
								>
									{{ $t('no') }} 👎</app-button
								>
							</div>
						</div>
					</div>
				</div>
			</div>
		</transition>
		<app-modal v-if="previewImage" @close="closeImage" @click.self="closeImage">
			<template v-slot:header>
				<h2>{{ previewImageName }}</h2>
			</template>
			<div class="flex flex-col items-center justify-center">
				<img :src="$filters.setImage(previewImage)" :alt="previewImageName" class="order-item-image-modal" />
			</div>
		</app-modal>
	</div>
</template>

<script>
	import OrderService from '@/services/OrderService';
	import AppIcon from '@/components/app/AppIcon';
	import { statuses } from '@/constants/mapOrderStatuses';
	import AppLoadingNoodles from '@/components/app/AppLoadingNoodles';
	import AppLine from '@/components/app/AppLine';
	import { mapGetters } from 'vuex';
	import AppButton from '@/components/app/AppButton.vue';
	import SmallOrderDetails from '@/components/orders/SmallOrderDetails.vue';
	import moment from 'moment';
	import OrderStatusProgressBar from '@/components/orders/OrderStatusProgressBar';
	import AppModal from '@/components/app/AppModal';

	export default {
		name: 'NewOrderModal',
		components: {
			AppModal,
			OrderStatusProgressBar,
			SmallOrderDetails,
			AppButton,
			AppLine,
			AppLoadingNoodles,
			AppIcon,
		},
		emits: ['updateLocalOrder', 'close', 'rejectOrder', 'openCookingTimeModal'],
		props: ['orderId', 'isDumb'],
		computed: {
			...mapGetters({
				user: 'user',
				loadingOrderId: 'loadingOrderId',
			}),
			isManagingOneBusiness() {
				return this.user?.restaurants?.length === 1;
			},
			getHeaderColor() {
				if (this.order.isScheduledOrder) {
					return '#f6941f';
				}
				if (this.order.status === statuses.rejected) {
					return '#ff1149';
				}
				if (this.order.isTakeAwayOrder) {
					return '#4387e7';
				}
				return '#2DB84C';
			},
			getHeaderText() {
				const pieces = [];
				if (this.order.isTakeAwayOrder) {
					pieces.push('Takeaway');
				}
				if (this.order.isScheduledOrder) {
					pieces.push('Scheduled');
				}
				if (pieces.length === 0) {
					pieces.push('Delivery');
				}
				if (this.order.status === statuses.rejected) {
					pieces.push('Rejected');
				}
				return pieces.join(', ');
			},
			getOrderTypes() {
				const pieces = [];
				const isCashless = this.order.orderExtraData?.cashless;
				if (this.order.paymentMethod.paymentType === 'CREDIT_CARD') {
					pieces.push({ icon: 'credit-card', label: 'Card' });
				}
				if (!isCashless) {
					pieces.push({ icon: 'cash', label: this.order.paymentMethod.paymentType || 'Cash' });
				}
				if (this.order.isTakeAwayOrder) {
					pieces.push({ icon: 'greeting', label: 'Takeaway' });
				} else {
					pieces.push({ icon: 'bike', label: 'Delivery' });
				}
				if (this.order.isScheduledOrder) {
					pieces.push({ icon: 'clock', label: 'Scheduled' });
				}
				if (isCashless) {
					pieces.push({ label: 'Cashless' });
				}
				return pieces;
			},
			formatOrderId() {
				return OrderService.formatOrderId(this.orderId);
			},
			calcEta() {
				return this.$filters.getShortTime(moment(this.order.date).add(this.order.deliverytime, 'minutes'));
			},
		},
		data: () => ({
			order: {},
			loading: false,
			statuses,
			previewImage: null,
			previewImageName: '',
		}),
		methods: {
			showImage(item) {
				this.previewImage = this.$filters.orderItemImage(item);
				this.previewImageName = item.itemName;
			},
			closeImage() {
				this.previewImage = null;
				this.previewImageName = '';
			},
			getOrderDetails(value) {
				if (value) {
					this.order = {};
					OrderService.getOrder(value)
						.then((order) => {
							this.order = order;
						})
						.catch((error) => {
							this.$notificationService.error(error);
						});
				} else {
					this.order = {};
				}
			},
			acceptOrder() {
				this.loading = true;
				this.$store.dispatch('setLoadingOrderId', this.orderId);
				if (this.order.status === statuses.rejected) {
					this.$emit('close');
				} else {
					OrderService.acceptOrder(this.orderId)
						.then(() => {
							this.$emit('updateLocalOrder', {
								orderId: this.orderId,
								props: {
									status: statuses.accepted,
								},
							});
						})
						.catch((error) => {
							this.$notificationService.error(error);
							this.$emit('close');
						})
						.finally(() => {
							this.loading = false;
							this.$store.dispatch('setLoadingOrderId', null);
						});
				}
			},
			acceptOrderAndSetTime() {
				if (!this.order.enableCookingTime) {
					this.acceptOrder();
				} else {
					this.$emit('openCookingTimeModal', {
						orderId: this.orderId,
						props: {
							status: statuses.accepted,
						},
					});
				}
			},
		},
		watch: {
			orderId(value) {
				value ? this.getOrderDetails(value) : null;
			},
		},
	};
</script>

<style scoped>
	.order-item-image-modal {
		max-width: calc(100vw - 224px);
		max-height: calc(100vh - 164px);
	}
</style>
